import fastq from 'fastq';

import { apiNeueDispatchAction } from 'src/utils/journeyApi';
import { useEditorContext } from 'src/store/editor';
import { useCurrentOrganization } from 'src/store/organization';
import { useEditorStore } from './editor-store';
import { ActionSource, Operation, Section, UserEditorAction } from './types';
import { sectionToJNodes } from './helpers/jnode/section-to-jnodes';
import { findSectionByBlockId } from './helpers/section/find-section-by-block-id';
import { findSectionById } from './helpers/section/find-section-by-id';
import { findSectionIndexByBlockId } from './helpers/section/find-section-index-by-block-id';
import { findSectionIndexById } from './helpers/section/find-section-index-by-id';
import { mapMutualActionPlanActionToServerAction } from './mutual-action-plans/server-action';
import { isMutualActonPlanAction, MutualActionPlanAction } from './mutual-action-plans/types';
import { Act } from '@testing-library/react-hooks';
import { SelectorIcon } from '@heroicons/react/outline';
import { findBlockById } from './helpers/block/find-block-by-id';

const BACKOFF_TIMEOUT_MS = 4000;
const MIN_TIMEOUT_MS = 50;
const BACKOFF_ITERATION = Math.floor(Math.log2(BACKOFF_TIMEOUT_MS / MIN_TIMEOUT_MS));

function timeout(iteration: number): number {
  if (iteration <= BACKOFF_ITERATION) {
    return MIN_TIMEOUT_MS * Math.pow(2, iteration);
  } else {
    return BACKOFF_TIMEOUT_MS;
  }
}
async function sendWithRetries(f: Function) {
  for (let i = 0; ; i++) {
    try {
      const result = await f();
      useEditorStore.getState().setOnline();
      return result;
    } catch (err: any) {
      if (err.message === 'Network Error') {
        console.log(err);
        useEditorStore.getState().setOffline();
        await new Promise((r) => setTimeout(r, timeout(i)));
      } else {
        return Promise.reject(err);
      }
    }
  }
}

const q = fastq.promise(async (actions: any[]) => {
  const result = await sendWithRetries(() => apiNeueDispatchAction(actions));
  return result;
}, 1);

export async function enqueNeueDispatchActions(actions: any[]): Promise<any> {
  return await q.push(actions);
}

function serverOperations(ops: Operation[], oldState: Section[], newState: Section[], source: ActionSource) {
  return ops.flatMap(op => {
    if (op.type === 'create-block') {
      const section = findSectionById(newState, op.sectionId)!;
      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(section, true))
        }
      }
    } else if (op.type === 'delete-block') {
      const section = findSectionById(newState, op.sectionId)!;
      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(section, true))
        }
      }
    } else if (op.type === 'move-block') {
      const oldFromSection = findSectionByBlockId(oldState, op.id)!;
      const newFromSection = findSectionById(newState, oldFromSection.id)!;
      const newToSection = findSectionById(newState, op.to.sectionId)!;

      if (oldFromSection.id === newToSection.id) {
        return [{
          type: 'set-step-blocks',
          payload: {
            step_uuid: newToSection.id,
            json: JSON.stringify(sectionToJNodes(newToSection, true)),
          }
        }]
      } else {
        return [{
          type: 'set-step-blocks',
          payload: {
            step_uuid: newFromSection.id,
            json: JSON.stringify(sectionToJNodes(newFromSection, true)),
          }
        }, {
          type: 'set-step-blocks',
          payload: {
            step_uuid: newToSection.id,
            json: JSON.stringify(sectionToJNodes(newToSection, true)),
          }
        }]
      }
    } else if (op.type === 'set-block-width') {
      const section = findSectionByBlockId(newState, op.id)!;
      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(section, true)),
        }
      }
    } else if (op.type === 'set-block-content') {
      const section = findSectionByBlockId(newState, op.id)!;
      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(section, true)),
        }
      }
    } else if (op.type === 'set-block-layout') {
      const section = findSectionByBlockId(newState, op.id)!;
      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(section, true)),
        }
      }
    } else if (op.type === 'delete-section') {
      return [
        {
          type: 'delete-step',
          payload: {
            step_uuid: op.id,
          },
        },
      ];
    } else if (op.type === 'create-section') {
        let previousSectionId = null;
        if (op.index > 0) {
          previousSectionId = newState[op.index - 1].id;
        }
        return [{
          type: 'create-step',
          payload: {
            step_uuid: op.id,
            step_name: null,
            previous_step_uuid: previousSectionId,
          },
        },
      ];
    } else if (op.type === 'move-section') {
      let newPreviousSectionId = null;
      if (op.to.index > 0) {
        newPreviousSectionId = newState[op.to.index - 1].id;
      }
      return [{
        type: 'move-step',
        payload: {
          step_uuid: op.id,
          new_previous_step_uuid: newPreviousSectionId,
        }
      }]
    } else if (op.type === 'set-section-name') {
      return [{
        type: 'set-step-name',
        payload: {
          step_uuid: op.id,
          name: op.name,
        }
      }]
    } else if (op.type === 'set-block-content-uuid') {
      const section = findSectionByBlockId(newState, op.id)!;
      const block = findBlockById(newState, op.id)!;
      const updatedBlock = {
        ...block,
        contentUUID: op.contentUUID,
      }
      const updatedSection = {
        ...section,
        blocks: section.blocks.map(b => b.id === op.id ? updatedBlock : b),
      }

      return {
        type: 'set-step-blocks',
        payload: {
          step_uuid: section.id,
          json: JSON.stringify(sectionToJNodes(updatedSection, true)),
        }
      }
    }
  })
}

export function mapToServerAction(
  action: UserEditorAction,
  oldState: Section[],
  newState: Section[],
  source: ActionSource
): any[] {
  if (isMutualActonPlanAction(action)) {
    return mapMutualActionPlanActionToServerAction(action as MutualActionPlanAction, oldState, newState, source);
  }

  console.log("updated action type", action.type);
  

  switch (action.type) {
    case 'set-image-url-after-upload': {
      const section = findSectionByBlockId(newState, action.id);
      if (!section) {
        return [];
      }
      return [
        {
          type: 'set-step-blocks',
          payload: {
            step_uuid: section.id,
            json: JSON.stringify(sectionToJNodes(section, true)),
          },
        },
      ];
    }
    case 'set-journey-name': {
      return [
        {
          type: 'set-journey-name',
          payload: {
            name: action.name,
          },
        },
      ];
    }
    case 'apply-operations': {
      return serverOperations(action.operations, oldState, newState, source);
    }
    default: {
      const exhaustiveCheck: any = action;
      throw new Error(`Unhandled case: ${JSON.stringify(exhaustiveCheck)}`);
    }
  }
}

export async function sync(action: UserEditorAction, oldState: Section[], newState: Section[], source: ActionSource) {
  const serverActions = mapToServerAction(action, oldState, newState, source);
  console.log('sendActionToServer', serverActions);
  const { journey } = useEditorContext.getState();
  const { currentOrganization } = useCurrentOrganization.getState();
  const context = { journey_uuid: journey.uuid, organization_id: currentOrganization.id };
  const preparedActions = serverActions.map((sa) => {
    return { action: sa, context: context };
  });
  try {
    return await enqueNeueDispatchActions(preparedActions);
  } catch (e) {
    console.error('Error dispatching action to server', e);
    return Promise.reject(`Error dispatching action to server: ${e}`);
  }
}
